/**
 * StepWrapper
 *
 * Provides the fixed bottom action bar shown on every setup step.
 * Children (the embedded settings panel) are scrollable above it.
 */

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Check, AlertTriangle } from 'lucide-react';

export type BarState = 'default' | 'blocked' | 'ready' | 'warning';

interface StepWrapperProps {
  children: React.ReactNode;
  // Bottom bar
  barState?: BarState;
  barMessage?: string;
  // Buttons
  showBack?: boolean;
  showSkip?: boolean;
  nextDisabled?: boolean;
  nextLabel?: string;
  onNext: () => void;
  onBack?: () => void;
  onSkip?: () => void;
}

export function StepWrapper({
  children,
  barState = 'default',
  barMessage,
  showBack = true,
  showSkip = true,
  nextDisabled = false,
  nextLabel = 'Next Step →',
  onNext,
  onBack,
  onSkip,
}: StepWrapperProps) {
  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {children}
      </div>

      {/* Fixed bottom action bar */}
      <div
        className={cn(
          'flex-shrink-0 border-t border-border px-8 py-4 flex items-center justify-between gap-4 bg-background',
        )}
      >
        {/* Left — back button */}
        <div className="w-28">
          {showBack && onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              ← Back
            </Button>
          )}
        </div>

        {/* Centre — status message */}
        <div className="flex-1 flex items-center justify-center gap-2 min-w-0">
          {barMessage && (
            <>
              {barState === 'blocked' && (
                <AlertTriangle className="h-4 w-4 text-warning-500 flex-shrink-0" />
              )}
              {barState === 'ready' && (
                <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
              )}
              {barState === 'warning' && (
                <AlertTriangle className="h-4 w-4 text-warning-500 flex-shrink-0" />
              )}
              <span
                className={cn(
                  'text-xs font-medium truncate',
                  barState === 'blocked' && 'text-warning-600',
                  barState === 'ready' && 'text-emerald-600',
                  barState === 'warning' && 'text-warning-600',
                  barState === 'default' && 'text-muted-foreground',
                )}
              >
                {barMessage}
              </span>
            </>
          )}
        </div>

        {/* Right — skip + next */}
        <div className="w-auto flex items-center gap-3">
          {showSkip && onSkip && (
            <Button variant="ghost" size="sm" className="text-muted-foreground text-xs" onClick={onSkip}>
              Skip for now
            </Button>
          )}
          <Button size="sm" disabled={nextDisabled} onClick={onNext}>
            {nextLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
